import React, { FC, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import Typography from '@mui/material/Typography';
import { BuyOrderModalProps } from '@src/types/common/modal.types';

import { useBalance } from '@services/contract/common/erc20.contract';
import {
  useBuyOrder,
  useCalculateFee,
  useGetOrder,
} from '@services/contract/fusion/marketplace.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import useEthPriceUsd from '@hooks/common/useEthPriceUsd';

import { modifyEvent } from '@helpers/common/form.helpers';

import {
  formatBNWithCommas,
  removeDecimals,
  toBN,
} from '@utils/formaters.utils';

import { nftAmountForPurchaseYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import LoadingTypography from '@components/common/LoadingTypography';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import SwitchChainButton from '@components/common/SwitchChainButton';

import { DEFAULT_CHAIN_ID } from '@constants/common/chain.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { ONLY_DIGITS_WITH_DECIMALS } from '@constants/common/regex.constants';
import {
  FUSION_NFT_NAME,
  MARKETPLACE_TOKEN,
} from '@constants/common/tokens.constants';
import { Size } from '@constants/common/ui.constants';
import { BUYER_MARKETPLACE_FEE } from '@constants/fusion/common.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Amount]: string;
};

const BuyOrderModal: FC<BuyOrderModalProps> = ({ orderId }) => {
  const { classes } = useStyles();
  const { account } = useAuth();

  const { data: order } = useGetOrder({
    chainId: DEFAULT_CHAIN_ID,
    orderId,
  });

  const { data: ehtPriceUsd } = useEthPriceUsd();
  const { data: calculateFee } = useCalculateFee({
    chainId: DEFAULT_CHAIN_ID,
    price: BigInt(order?.pricePerOne || 0),
    amount: BigInt(1),
  });

  const { writeContractAsync: buyOrder, isPending: isPendingBuyOrder } =
    useBuyOrder();

  const availableAmount = order?.amount;

  const formatPricePerOne = useMemo(() => {
    if (!order) return undefined;

    return toBN(order?.pricePerOne, MARKETPLACE_TOKEN.decimals);
  }, [order]);

  const formatPricePerOneUsd = useMemo(() => {
    if (!formatPricePerOne || !ehtPriceUsd) return undefined;

    return formatPricePerOne.times(ehtPriceUsd);
  }, [formatPricePerOne, ehtPriceUsd]);

  const info = [
    {
      title: 'Available Amount',
      value: `${availableAmount} ${FUSION_NFT_NAME}${availableAmount && Number(availableAmount) > 1 ? 's' : ''}`,
    },
    {
      title: `Price per ${FUSION_NFT_NAME}`,
      value:
        order && ehtPriceUsd
          ? `
            ${formatBNWithCommas(formatPricePerOne, 5, 0, true, true)} 
            ${MARKETPLACE_TOKEN.symbol} 
            ($${formatBNWithCommas(formatPricePerOneUsd, 2, 0, true, true)})
          `
          : undefined,
    },
  ];

  const {
    balance,
    isPending: isPendingBalance,
    refetch: refetchBalance,
  } = useBalance({ chainId: DEFAULT_CHAIN_ID });

  const validationSchema = yup.object().shape({
    amount: nftAmountForPurchaseYup({
      balance: toBN(balance),
      nftAmount: toBN(availableAmount),
      price: toBN(calculateFee?.totalAmount),
      decimal: MARKETPLACE_TOKEN.decimals,
    }),
  });

  const {
    control,
    handleSubmit,
    trigger,
    setValue,
    formState: { isValid },
    watch,
    reset,
  } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: { [FieldNames.Amount]: '' },
  });

  const inputAmount = watch(FieldNames.Amount);

  const totalAmount = useMemo(() => {
    if (!calculateFee || !inputAmount) return undefined;

    return removeDecimals(
      toBN(inputAmount).times(calculateFee.totalAmount),
      MARKETPLACE_TOKEN.decimals,
    );
  }, [inputAmount, calculateFee]);

  const totalAmountUsd = useMemo(() => {
    if (!totalAmount || !ehtPriceUsd) return undefined;

    return totalAmount.times(ehtPriceUsd);
  }, [totalAmount, ehtPriceUsd]);

  const fee = useMemo(() => {
    if (!calculateFee || !inputAmount) return undefined;
    return toBN(inputAmount).times(
      toBN(calculateFee.buyerFeeAmount, MARKETPLACE_TOKEN.decimals),
    );
  }, [calculateFee, inputAmount]);

  const onSubmit = async (formData: FormValues) => {
    if (!formData.amount || !order?.pricePerOne) return;
    await trigger();
    await buyOrder({
      orderId: BigInt(orderId),
      amount: BigInt(formData.amount),
      pricePerOne: BigInt(order?.pricePerOne),
    });

    reset();
    await refetchBalance();
    modalActions.hide();
  };

  const onMaxHandlerStake = useCallback(() => {
    if (!account || !availableAmount || toBN(availableAmount).isZero()) return;
    setValue(FieldNames.Amount, availableAmount, {
      shouldValidate: true,
    });
  }, [availableAmount, account]);

  const isDisableButton = !balance || toBN(balance).isZero() || !isValid;

  return (
    <ModalWrapper title="Buy">
      <form onSubmit={handleSubmit(onSubmit)} className={classes.container}>
        <div className={classes.info}>
          {info.map((item) => (
            <div className={classes.row} key={item.title}>
              <Typography variant="body2">{item.title}</Typography>
              <LoadingTypography variant="subtitle2" isLoading={!item.value}>
                {item.value}
              </LoadingTypography>
            </div>
          ))}
        </div>
        <div className={classes.inputContainer}>
          <div className={classes.balance}>
            <LoadingTypography
              variant="caption1"
              color={(theme) => theme.palette.custom.text.accent}
              isLoading={isPendingBalance}
            >
              {`Balance: ${formatBNWithCommas(balance, 5, MARKETPLACE_TOKEN.decimals)}`}{' '}
              {MARKETPLACE_TOKEN.symbol}
            </LoadingTypography>
          </div>
          <Controller
            control={control}
            name={FieldNames.Amount}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                value={value}
                onBlur={onBlur}
                onChange={(e) =>
                  onChange(modifyEvent(e, ONLY_DIGITS_WITH_DECIMALS))
                }
                placeholder="0"
                onMax={onMaxHandlerStake}
                inputError={error?.message}
                disabled={!account}
              />
            )}
          />
        </div>
        <div className={classes.totalInfo}>
          <div className={classes.row}>
            <Typography variant="subtitle1">Fee</Typography>
            <Typography variant="subtitle1">
              {BUYER_MARKETPLACE_FEE}%{' '}
              {fee &&
                `(${formatBNWithCommas(fee, 5, 0, true, true)} ${MARKETPLACE_TOKEN.symbol})`}
            </Typography>
          </div>
          <div className={classes.row}>
            <Typography variant="subtitle1">Total</Typography>
            {totalAmount && isValid ? (
              <Typography variant="subtitle1">
                {formatBNWithCommas(totalAmount, 5, 0, true)}{' '}
                {MARKETPLACE_TOKEN.symbol}
                {`($${formatBNWithCommas(totalAmountUsd, 2, 0, true)})`}
              </Typography>
            ) : (
              <Typography variant="subtitle1">-</Typography>
            )}
          </div>
        </div>
        <SwitchChainButton chainId={DEFAULT_CHAIN_ID} size={Size.Large}>
          <Button
            disabled={isDisableButton}
            buttonType="submit"
            isLoading={isPendingBuyOrder}
          >
            Buy
          </Button>
        </SwitchChainButton>
      </form>
    </ModalWrapper>
  );
};

export default BuyOrderModal;
